import { useMemo, useState } from "react";
import {
  Accordion,
  AskStrikeUpButton,
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  HStack,
  Table,
  Tabs,
  Text,
  VStack,
} from "components/ui";
import { useTranslation } from "hooks/useTranslation";
import { MdSearch } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import helpContent from "./helpContent";
import { TechnicalDocsTab } from "./components/TechnicalDocsTab";

const HelpPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  // Deep-link support: /admin/help?tab=technical-docs opens that tab on load.
  const initialTab = searchParams.get("tab") ?? "guide";

  const filteredSections = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return helpContent.sections;
    return helpContent.sections.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.steps.some((st) => st.text.toLowerCase().includes(q)) ||
        s.faqs.some(
          (f) =>
            f.question.toLowerCase().includes(q) ||
            f.answer.toLowerCase().includes(q),
        ),
    );
  }, [search]);

  return (
    <Flex direction="column" gap={6} w="full">
      {/* Header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
        <Box>
          <Heading size="2xl">{t("app.help.title")}</Heading>
          <Text color="gray.500" mt={1} fontSize="sm">
            {t("app.help.lastUpdated")}: {helpContent.lastUpdated}
          </Text>
        </Box>
        <HStack gap={3}>
          <AskStrikeUpButton context={t("app.askStrikeUp.context.help")} />
          <Flex
            align="center"
            gap={2}
            border="1px solid"
            borderColor="border.default"
            borderRadius="md"
            px={3}
            py={1.5}
            maxW="300px"
            w="full"
          >
            <MdSearch size={16} />
            <input
              placeholder={t("app.help.searchPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                background: "transparent",
                outline: "none",
                border: "none",
                width: "100%",
                fontSize: "0.875rem",
              }}
            />
          </Flex>
        </HStack>
      </Flex>

      <Tabs.Root defaultValue={initialTab}>
        <Tabs.List>
          <Tabs.Trigger value="guide">{t("app.help.tabs.guide")}</Tabs.Trigger>
          <Tabs.Trigger value="technical-docs">
            {t("app.help.tabs.technicalDocs")}
          </Tabs.Trigger>
          <Tabs.Trigger value="faq">{t("app.help.tabs.faq")}</Tabs.Trigger>
          <Tabs.Trigger value="troubleshooting">
            {t("app.help.tabs.troubleshooting")}
          </Tabs.Trigger>
          <Tabs.Trigger value="whats-new">
            {t("app.help.tabs.whatsNew")}
          </Tabs.Trigger>
        </Tabs.List>

        {/* GUIDE TAB */}
        <Tabs.Content value="guide">
          <Flex direction="column" gap={5} mt={4}>
            {/* Quick Start — hidden when searching */}
            {!search && (
              <Card.Root>
                <Card.Header>
                  <Heading size="md">{t("app.help.quickStart.title")}</Heading>
                  <Text color="gray.500" fontSize="sm">
                    {t("app.help.quickStart.subtitle")}
                  </Text>
                </Card.Header>
                <Card.Body pt={0}>
                  <VStack align="stretch" gap={3}>
                    {helpContent.quickStart.map((step, i) => (
                      <HStack key={i} align="start" gap={3}>
                        <Badge
                          borderRadius="full"
                          minW="26px"
                          h="26px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          colorScheme="brand"
                          flexShrink={0}
                        >
                          {i + 1}
                        </Badge>
                        <Box>
                          <Text fontSize="sm">{step.text}</Text>
                          {step.tip && (
                            <Text fontSize="xs" color="blue.500" mt={0.5}>
                              Tip: {step.tip}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </Card.Body>
              </Card.Root>
            )}

            {/* Feature sections */}
            <Accordion.Root multiple>
              {filteredSections.map((s) => (
                <Accordion.Item key={s.id} value={s.id}>
                  <Accordion.ItemTrigger>
                    <Box flex={1} textAlign="left">
                      <Text fontWeight="semibold">{s.title}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {s.description}
                      </Text>
                    </Box>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <VStack align="stretch" gap={3} pb={3}>
                      <VStack align="stretch" gap={2}>
                        {s.steps.map((step, i) => (
                          <HStack key={i} align="start" gap={3}>
                            <Badge
                              borderRadius="full"
                              minW="22px"
                              h="22px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              colorScheme="gray"
                              flexShrink={0}
                              fontSize="xs"
                            >
                              {i + 1}
                            </Badge>
                            <Box>
                              <Text fontSize="sm">{step.text}</Text>
                              {step.tip && (
                                <Text fontSize="xs" color="blue.500" mt={0.5}>
                                  Tip: {step.tip}
                                </Text>
                              )}
                            </Box>
                          </HStack>
                        ))}
                      </VStack>
                    </VStack>
                  </Accordion.ItemContent>
                </Accordion.Item>
              ))}
            </Accordion.Root>

            {filteredSections.length === 0 && (
              <Text color="gray.500" textAlign="center" py={10}>
                {t("app.help.noResults")} &ldquo;{search}&rdquo;
              </Text>
            )}
          </Flex>
        </Tabs.Content>

        {/* TECHNICAL DOCS TAB */}
        <Tabs.Content value="technical-docs">
          <TechnicalDocsTab />
        </Tabs.Content>

        {/* FAQ TAB */}
        <Tabs.Content value="faq">
          <Accordion.Root multiple mt={4}>
            {filteredSections.flatMap((s) =>
              s.faqs.map((faq, i) => (
                <Accordion.Item key={`${s.id}-${i}`} value={`${s.id}-${i}`}>
                  <Accordion.ItemTrigger>
                    <HStack gap={2} flex={1} textAlign="left">
                      <Badge size="sm" colorScheme="gray" flexShrink={0}>
                        {s.title}
                      </Badge>
                      <Text fontWeight="medium" fontSize="sm">
                        {faq.question}
                      </Text>
                    </HStack>
                    <Accordion.ItemIndicator />
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent>
                    <Text fontSize="sm" pb={3}>
                      {faq.answer}
                    </Text>
                  </Accordion.ItemContent>
                </Accordion.Item>
              )),
            )}
          </Accordion.Root>
        </Tabs.Content>

        {/* TROUBLESHOOTING TAB */}
        <Tabs.Content value="troubleshooting">
          <Box mt={4} overflowX="auto">
            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t("app.help.troubleshooting.symptom")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.help.troubleshooting.cause")}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t("app.help.troubleshooting.solution")}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {helpContent.troubleshooting.map((entry, i) => (
                  <Table.Row key={i}>
                    <Table.Cell fontWeight="medium" fontSize="sm">
                      {entry.symptom}
                    </Table.Cell>
                    <Table.Cell color="orange.500" fontSize="sm">
                      {entry.cause}
                    </Table.Cell>
                    <Table.Cell fontSize="sm">{entry.solution}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </Tabs.Content>

        {/* WHAT'S NEW TAB */}
        <Tabs.Content value="whats-new">
          <VStack align="stretch" gap={3} mt={4}>
            {[...helpContent.changelog].reverse().map((entry, i) => (
              <Card.Root key={i}>
                <Card.Body py={3}>
                  <HStack gap={3}>
                    <Badge colorScheme="brand" flexShrink={0}>
                      {entry.date}
                    </Badge>
                    <Box>
                      <Text fontWeight="semibold" fontSize="sm">
                        {entry.feature}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        {entry.description}
                      </Text>
                    </Box>
                  </HStack>
                </Card.Body>
              </Card.Root>
            ))}
          </VStack>
        </Tabs.Content>
      </Tabs.Root>
    </Flex>
  );
};

export default HelpPage;
